import * as React from 'react';
import * as styles from './EditableTitle.css';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { initializeIcons } from 'office-ui-fabric-react/lib/Icons';
import { IAColorPicker } from '../colorPicker/ColorPicker';
import { LocalizationService } from '../../services/LocalizationService';

initializeIcons();

export interface IEditableTitleChanges {
  title?: string;
  headLineColor?: string;
  headlineFontSize?: number;
  headlineFontStyle?: string;
  headlineTextAlign?: string;
  headlineFontWeight?: string;
  headlineTextDecoration?: string;
  headlineLineHeight?: number;
  headlineTag?: string;
}

export interface IEditableTitleProps {
  show: boolean;
  isEditMode: boolean;
  title?: string;
  permalinkId?: string;
  inlineWithContent?: boolean;
  rightElement?: React.ReactNode;
  headLineColor?: string;
  headlineFontSize?: number;
  headlineFontStyle?: string;
  headlineTextAlign?: string;
  headlineFontWeight?: string;
  headlineTextDecoration?: string;
  headlineLineHeight?: number;
  headlineTag?: string;
  themeColors?: string[];
  fontFamily?: string;
  hasDarkBackground?: boolean;
  onChange: (changes: IEditableTitleChanges) => void;
}

type MenuKey = "heading" | "size" | "color" | "spacing" | "align";
type OpenMenu = MenuKey | null;

export interface IEditableTitleState {
  isEditing: boolean;
  openMenu: OpenMenu;
  currentTitle?: string;
}

interface IHeadingPreset {
  label: string;
  tag: "h1" | "h2" | "h3" | "h4" | "p";
}

const SIZE_OPTIONS: number[] = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 42, 68];

const TAG_DEFAULT_SIZES: { [tag: string]: number } = { h1: 32, h2: 28, h3: 24, h4: 20, p: 18 };

const LINE_HEIGHT_OPTIONS: number[] = [1, 1.2, 1.4, 1.6, 2, 2.4];

const TEXT_ALIGN_OPTIONS: { label: string; value: string; icon: string }[] = [
  { label: 'Left', value: 'left', icon: 'AlignLeft' },
  { label: 'Center', value: 'center', icon: 'AlignCenter' },
  { label: 'Right', value: 'right', icon: 'AlignRight' }
];

export class IAEditableTitle extends React.Component<IEditableTitleProps, IEditableTitleState> {

  private readonly wrapperRef: React.RefObject<HTMLDivElement>;
  private readonly textRef: React.RefObject<HTMLDivElement>;
  private readonly toolbarRef: React.RefObject<HTMLDivElement>;
  private cancelEdit: boolean;
  private localizationService: LocalizationService = new LocalizationService();

  constructor(props: IEditableTitleProps) {
    super(props);
    this.wrapperRef = React.createRef();
    this.textRef = React.createRef();
    this.toolbarRef = React.createRef();
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.state = {
      isEditing: false,
      openMenu: null,
      currentTitle: props.title
    };
  }

  public componentDidMount(): void {
    document.addEventListener('click', this.handleClickOutside, true);
  }

  private getHeadingPresets(): IHeadingPreset[] {
    return [
      { label: `${this.localizationService.strings.Editor_Title} 1`, tag: "h1" },
      { label: `${this.localizationService.strings.Editor_Title} 2`, tag: "h2" },
      { label: `${this.localizationService.strings.Editor_Title} 3`, tag: "h3" },
      { label: `${this.localizationService.strings.Editor_Title} 4`, tag: "h4" },
      { label: "Normal", tag: "p" }
    ];
  }

  public componentWillUnmount(): void {
    document.removeEventListener('click', this.handleClickOutside, true);
  }

  public componentDidUpdate(prevProps: IEditableTitleProps): void {
    if (prevProps.title !== this.props.title && !this.state.isEditing) {
      this.setState({ currentTitle: this.props.title });
    }
  }

  private isInside(event: Event, element: HTMLElement | null): boolean {
    if (!element) {
      return false;
    }
    return element.contains(event.target as Node);
  }

  private handleClickOutside(event: Event): void {
    if (this.state.openMenu && !this.isInside(event, this.toolbarRef.current)) {
      this.setState({ openMenu: null });
    }
    if (this.state.isEditing && !this.isInside(event, this.wrapperRef.current)) {
      this.commitEdit();
    }
  }

  private toggleMenu(menu: MenuKey): void {
    this.setState({ openMenu: this.state.openMenu === menu ? null : menu });
  }

  private applyChange(changes: IEditableTitleChanges): void {
    this.setState({ openMenu: null });
    this.props.onChange(changes);
  }

  private applyColorChange(headLineColor: string): void {
    this.props.onChange({ headLineColor });
  }

  private startEdit(): void {
    this.cancelEdit = false;
    this.setState({ isEditing: true }, () => {
      if (this.textRef.current) {
        this.textRef.current.innerText = this.state.currentTitle ?? '';
        this.textRef.current.focus();
        const range = document.createRange();
        range.selectNodeContents(this.textRef.current);
        range.collapse(false);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    });
  }

  private commitEdit(): void {
    if (!this.state.isEditing) {
      return;
    }
    const newTitle = this.textRef.current?.innerText.trim() ?? this.props.title;
    if (this.cancelEdit) {
      this.setState({ isEditing: false, openMenu: null });
      return;
    }
    if (newTitle !== this.props.title) {
      this.setState({ isEditing: false, openMenu: null, currentTitle: newTitle });
      this.props.onChange({ title: newTitle });
    } else {
      this.setState({ isEditing: false, openMenu: null });
    }
  }

  private onKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitEdit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit = true;
      this.commitEdit();
    }
  }

  public render(): JSX.Element {
    if (!this.props.show && !this.props.isEditMode) {
      return null;
    }
    if (!this.props.show && !this.props.title) {
      return null;
    }

    const fontWeight = this.props.headlineFontWeight === 'regular' ? 400 : 600;
    const fontStyle = this.props.headlineFontStyle === 'italic' ? 'italic' : 'normal';
    const textDecoration = this.props.headlineTextDecoration === 'underline' ? 'underline' : 'none';
    const textAlign = this.props.headlineTextAlign === 'center' ? 'center' : this.props.headlineTextAlign === 'right' ? 'right' : 'left';
    const activeTag = (this.props.headlineTag ?? "h2") as "h1" | "h2" | "h3" | "h4" | "p";
    const TitleTag = activeTag;
    const activeFontSize = this.props.headlineFontSize ?? (this.props.headlineTag === undefined ? 20 : TAG_DEFAULT_SIZES[activeTag]);
    const activeLineHeight = this.props.headlineLineHeight ?? 1.6;
    const headingPresets = this.getHeadingPresets();
    const headingPreset = headingPresets.find((preset) => preset.tag === activeTag);
    const headingLabel = headingPreset ? headingPreset.label : "Custom";
    const activeColor = this.props.headLineColor ? this.props.headLineColor : (this.props.hasDarkBackground ? "#ffffff" : "#333333");
    const canEdit = this.props.isEditMode;
    const displayTitle = this.state.currentTitle ?? this.props.title;

    return (
        <div
          className={styles.IA_EditableTitleWrapper + (this.props.inlineWithContent ? " " + styles.IA_EditableTitleWrapperInline : "")}
          ref={this.wrapperRef}
          style={this.state.isEditing ? { paddingTop: 5 } : undefined}
        >
        <div
          className={styles.IA_EditableTitleText + (this.state.isEditing ? " " + styles.IA_EditableTitleTextEditing : "")}
          style={{
            color: activeColor,
            fontSize: activeFontSize,
            fontStyle,
            fontWeight,
            textDecoration,
            textAlign,
            lineHeight: activeLineHeight,
            fontFamily: this.props.fontFamily
          }}
          onClick={canEdit && !this.state.isEditing ? () => this.startEdit() : undefined}
        >
          {this.state.isEditing ?
            <div
              ref={this.textRef}
              className={styles.IA_EditableTitleInput}
              contentEditable
              suppressContentEditableWarning
              spellCheck={false}
              onKeyDown={(event) => this.onKeyDown(event)}
            />
            :
            <TitleTag
              id={this.props.permalinkId}
              style={{
                margin: 0,
                fontSize: 'inherit',
                fontWeight: 'inherit',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {displayTitle
                ? <>
                    {displayTitle}
                    {this.props.permalinkId && !canEdit &&
                      <a
                        href={`#${this.props.permalinkId}`}
                        className={styles.IA_EditableTitlePermalink}
                        style={{ color: activeColor }}
                        title={`${this.localizationService.strings.Editor_Permalink} ${displayTitle}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Icon iconName="Link" />
                      </a>
                    }
                  </>
                : canEdit
                  ? <span className={styles.IA_EditableTitlePlaceholder}>{this.localizationService.strings.Editor_AddTitlePlaceholder}</span>
                  : ''}
            </TitleTag>
          }
        </div>
        {this.props.inlineWithContent && this.props.rightElement &&
          <div className={styles.IA_EditableTitleRightElement}>
            {this.props.rightElement}
          </div>
        }
        {this.state.isEditing &&
          <div className={styles.IA_EditableTitleToolbar} ref={this.toolbarRef}>
            <div className={styles.IA_EditableTitleGroup}>
              <div className={styles.IA_EditableTitleDropdown}>
                <div className={styles.IA_EditableTitleDropdownButton} onClick={() => this.toggleMenu('heading')}>
                  <span>{headingLabel}</span>
                  <Icon iconName="ChevronDown" className={styles.IA_EditableTitleChevron} />
                </div>
                {this.state.openMenu === 'heading' &&
                  <div className={styles.IA_EditableTitleMenu}>
                    {this.getHeadingPresets().map((preset) => (
                      <div
                        key={preset.label}
                        className={styles.IA_EditableTitleMenuItem + (activeTag === preset.tag ? " " + styles.IA_active : "")}
                        onClick={() => this.applyChange({ headlineTag: preset.tag, headlineFontSize: undefined })}
                      >
                        {preset.label}
                      </div>
                    ))}
                  </div>
                }
              </div>
              <div className={styles.IA_EditableTitleDropdown + " " + styles.IA_EditableTitleSizeDropdown}>
                <div className={styles.IA_EditableTitleDropdownButton} onClick={() => this.toggleMenu('size')}>
                  <span>{activeFontSize}</span>
                  <Icon iconName="ChevronDown" className={styles.IA_EditableTitleChevron} />
                </div>
                {this.state.openMenu === 'size' &&
                  <div className={styles.IA_EditableTitleMenu + " " + styles.IA_EditableTitleSizeMenu}>
                    {SIZE_OPTIONS.map((size) => (
                      <div
                        key={size}
                        className={styles.IA_EditableTitleMenuItem + (activeFontSize === size ? " " + styles.IA_active : "")}
                        onClick={() => this.applyChange({ headlineFontSize: size })}
                      >
                        {size}
                      </div>
                    ))}
                  </div>
                }
              </div>
            </div>

            <div className={styles.IA_EditableTitleDivider} />

            <div className={styles.IA_EditableTitleGroup}>
              <div
                className={styles.IA_EditableTitleIconButton + (fontWeight === 600 ? " " + styles.IA_active : "")}
                title={this.localizationService.strings.Editor_Bold}
                onClick={() => this.applyChange({ headlineFontWeight: fontWeight === 600 ? 'regular' : 'bold' })}
              >
                <span style={{ fontWeight: 'bold' }}>B</span>
              </div>
              <div
                className={styles.IA_EditableTitleIconButton + (fontStyle === 'italic' ? " " + styles.IA_active : "")}
                title={this.localizationService.strings.Editor_Italic}
                onClick={() => this.applyChange({ headlineFontStyle: fontStyle === 'italic' ? undefined : 'italic' })}
              >
                <span style={{ fontStyle: 'italic', fontFamily: 'Georgia, serif', fontSize: 16, lineHeight: 1, marginTop: 3 }}>I</span>
              </div>
              <div
                className={styles.IA_EditableTitleIconButton + (textDecoration === 'underline' ? " " + styles.IA_active : "")}
                title={this.localizationService.strings.Editor_Underline}
                onClick={() => this.applyChange({ headlineTextDecoration: textDecoration === 'underline' ? undefined : 'underline' })}
              >
                <span style={{ textDecoration: 'underline' }}>U</span>
              </div>
              <div className={styles.IA_EditableTitleDropdown}>
                <div className={styles.IA_EditableTitleColorButton} title={this.localizationService.strings.Editor_TextColor} onClick={() => this.toggleMenu('color')}>
                  <span className={styles.IA_EditableTitleColorLetter} style={{ color: activeColor }}>A</span>
                  <Icon iconName="ChevronDown" className={styles.IA_EditableTitleChevron} style={{ fontSize: 10 }} />
                </div>
                {this.state.openMenu === 'color' &&
                  <div className={styles.IA_EditableTitleMenu + " " + styles.IA_EditableTitleColorMenu} onMouseDown={(e) => e.stopPropagation()}>
                    {this.props.themeColors?.length > 0 &&
                      <>
                        <div className={styles.IA_EditableTitleColorGroupLabel}>{this.localizationService.strings.Editor_ThemeColors}</div>
                        <div className={styles.IA_EditableTitleColorSwatches}>
                          {this.props.themeColors.map((color, index) => (
                            <div
                              key={"theme-" + index}
                              className={styles.IA_EditableTitleColorSwatch + (activeColor === color ? " " + styles.IA_active : "")}
                              style={{ backgroundColor: color }}
                              onClick={() => this.applyColorChange(color)}
                            />
                          ))}
                        </div>
                      </>
                    }

                    <div className={styles.IA_EditableTitleColorGroupLabel}>{this.localizationService.strings.Editor_CustomColor}</div>
                    <IAColorPicker
                      color={activeColor}
                      showClearButton={false}
                      showOpacity={false}
                      displayInline
                      styles={{ width: "auto", margin: 0, float: "none" }}
                      onChange={(color: string) => this.applyColorChange(color)}
                    />
                  </div>
                }
              </div>
              <div
                className={styles.IA_EditableTitleIconButton}
                title={this.localizationService.strings.Editor_ClearFormatting}
                onClick={() => this.applyChange({
                  headlineFontSize: undefined,
                  headlineFontWeight: undefined,
                  headlineFontStyle: undefined,
                  headlineTextDecoration: undefined,
                  headlineTextAlign: undefined,
                  headlineLineHeight: undefined,
                  headLineColor: undefined
                })}
              >
                <Icon iconName="ClearFormatting" />
              </div>
            </div>

            <div className={styles.IA_EditableTitleDivider} />

            <div className={styles.IA_EditableTitleGroup}>
              <div className={styles.IA_EditableTitleDropdown}>
                <div className={styles.IA_EditableTitleIconButton} title={this.localizationService.strings.Editor_LineSpacing} style={{ width: 'auto', padding: '0 8px' }} onClick={() => this.toggleMenu('spacing')}>
                  <Icon iconName="LineSpacing" />
                  <Icon iconName="ChevronDown" className={styles.IA_EditableTitleChevron} style={{ fontSize: 10 }} />
                </div>
                {this.state.openMenu === 'spacing' &&
                  <div className={styles.IA_EditableTitleMenu + " " + styles.IA_EditableTitleSpacingMenu}>
                    {LINE_HEIGHT_OPTIONS.map((lineHeight) => (
                      <div
                        key={lineHeight}
                        className={styles.IA_EditableTitleMenuItem + (activeLineHeight === lineHeight ? " " + styles.IA_active : "")}
                        onClick={() => this.applyChange({ headlineLineHeight: lineHeight })}
                      >
                        {lineHeight}
                      </div>
                    ))}
                  </div>
                }
              </div>

              <div className={styles.IA_EditableTitleDropdown}>
                <div className={styles.IA_EditableTitleIconButton} title="Text Alignment" style={{ width: 'auto', padding: '0 8px' }} onClick={() => this.toggleMenu('align')}>
                  <Icon iconName={TEXT_ALIGN_OPTIONS.find(opt => opt.value === textAlign)?.icon || 'AlignLeft'} />
                  <Icon iconName="ChevronDown" className={styles.IA_EditableTitleChevron} style={{ fontSize: 10 }} />
                </div>
                {this.state.openMenu === 'align' &&
                  <div className={styles.IA_EditableTitleMenu}>
                    {TEXT_ALIGN_OPTIONS.map((option) => (
                      <div
                        key={option.value}
                        className={styles.IA_EditableTitleMenuItem + (textAlign === option.value ? " " + styles.IA_active : "")}
                        onClick={() => this.applyChange({ headlineTextAlign: option.value })}
                      >
                        <Icon iconName={option.icon} style={{ marginRight: 8 }} />
                        {option.label}
                      </div>
                    ))}
                  </div>
                }
              </div>
            </div>
          </div>
        }
      </div>
    );
  }
}
